/**
 * Where we are in the storyline — a row of page pips under the story's title.
 * Pages already read settle into warm ink, the current page glows like the
 * reading lamp, and the pages still ahead wait as faint rings. Long stories
 * (more pips than a phone header can hold) fall back to a slim thread that
 * fills instead.
 *
 * Deliberately NOT tappable: navigation stays with the page-turn ritual; this
 * only answers "how far along are we?" at a glance. Everything here rests in
 * its settled style, so reduced motion changes nothing.
 */
const MAX_PIPS = 14;

export function StoryStepper({ page, total }: { page: number; total: number }) {
  if (total <= 1) return null;
  const current = Math.min(Math.max(1, page), total);

  const label = `Page ${current} of ${total}`;

  if (total > MAX_PIPS) {
    return (
      <div
        className="stepper stepper--thread"
        role="progressbar"
        aria-valuemin={1}
        aria-valuemax={total}
        aria-valuenow={current}
        aria-valuetext={label}
      >
        <span
          className="stepper__fill"
          style={{ width: `${(current / total) * 100}%` }}
        />
      </div>
    );
  }

  return (
    <div
      className="stepper"
      role="progressbar"
      aria-valuemin={1}
      aria-valuemax={total}
      aria-valuenow={current}
      aria-valuetext={label}
    >
      {Array.from({ length: total }, (_, i) => {
        const n = i + 1;
        const cls =
          n < current
            ? "stepper__pip stepper__pip--read"
            : n === current
              ? "stepper__pip stepper__pip--now"
              : "stepper__pip";
        return <span key={n} className={cls} />;
      })}
    </div>
  );
}
